import React from 'react';
import isNil from 'lodash/isNil';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import {
  FieldContext,
  IFilterFieldsFragment,
  PermissionKey,
} from '../../../../../../utils/apollo/graphqlSchema';
import { setBreakdownGroupFilter } from '../../../../../../redux/slices/oldBuilder';
import {
  activeSchemaIdSelector,
  defaultLogOpsSelector,
} from '../../../../../../redux/selectors/builder';
import { assertCondition } from '../../../../../../utils/types';
import { defaultEventInput } from '../../../../../../utils/builder';
import FilterWidget from '../../../../../FilterWidget';
import { IPredicateContext } from '../../../../../FilterWidget/types';
import Popover from '../../../../../Popover';
import { IconWrapper, SvgIcon } from '../../../../../Icon';
import { useActionsVisibilityContext } from '../../../../../ActionsVisibilityContext';
import usePermission from '../../../../../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../../../../../pages/PermissionControls/PermissionElementWrapper';
import usePopoverController from '../../../../../Popover/usePopoverController';
import KubitTooltip from '../../../../../KubitTooltip';

interface IProps {
  groupIndex: number;
}

const predicateContext: IPredicateContext = {
  fieldContext: FieldContext.Breakdown,
  events: [defaultEventInput],
  func: null,
};

export default ({ groupIndex }: IProps) => {
  const { closePopover, isPopoverOpen, openPopover, popoverAnchor } =
    usePopoverController<HTMLDivElement>();

  const dispatch = useAppDispatch();

  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const { checkIsPermitted } = usePermission();
  const isPermittedToReadField = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FieldRead],
    schemaId: activeSchemaId,
  });

  const defaultLogOps = useAppSelector(defaultLogOpsSelector);

  const groups = useAppSelector(({ builder }) => builder.breakdown.groups);

  const groupField = groups?.[groupIndex]?.field;
  const groupFilter = groups?.[groupIndex]?.filter;

  const { hideActions } = useActionsVisibilityContext();

  const handleIconClick = () => {
    if (groupFilter || !isPermittedToReadField) {
      return;
    }

    hideActions();

    openPopover();
  };

  const handleSubmit = (newFilter: IFilterFieldsFragment) => {
    assertCondition(newFilter.__typename === 'BasicFilter');

    const predicate = newFilter.conditions[0].predicates[0];

    dispatch(
      setBreakdownGroupFilter({
        groupIndex,
        filter: predicate,
      }),
    );

    closePopover();
  };

  return (
    <div ref={popoverAnchor}>
      <KubitTooltip
        content="Add Breakdown Filter"
        placement="top-start"
        disabled={!isPermittedToReadField}
        noOpenDelay={true}
      >
        <PermissionElementWrapper isPermitted={isPermittedToReadField}>
          <IconWrapper
            onClick={isPermittedToReadField ? handleIconClick : undefined}
          >
            <SvgIcon
              name="filter"
              isActive={isPopoverOpen || !isNil(groupFilter)}
              width={16}
              height={16}
              isDisabled={!isPermittedToReadField}
              customFill="currentColor"
            />
          </IconWrapper>
        </PermissionElementWrapper>
      </KubitTooltip>
      {isPopoverOpen && groupField && (
        <Popover anchor={popoverAnchor.current} isOpen close={closePopover}>
          <FilterWidget
            onSubmit={handleSubmit}
            schemaId={activeSchemaId}
            context={predicateContext}
            lockedField={groupField}
            maxConditions={1}
            maxPredicates={1}
            defaultFilter={null}
            disableNamedFilters={true}
            defaultLogOps={defaultLogOps}
          />
        </Popover>
      )}
    </div>
  );
};
